/* eslint-disable @typescript-eslint/no-explicit-any */
import type { StandardSchemaV1 } from '@standard-schema/spec';
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import axios from 'axios';
import humps, { type HumpsProcessor } from 'humps';
import isBoolean from 'lodash/isBoolean';
import type { MaybeRefOrGetter } from 'vue';
import { isRef, nextTick, toValue } from 'vue';

import { useIdentityStore } from '@/stores/identity';
import { IMPERSONATED_USER_ID_KEY, useImpersonatorStore } from '@/stores/impersonator';
import { useTokenStore } from '@/stores/token';
import type { AxiosOptions } from '@/types/axios';
import type { HttpError } from '@/types/http';
import { browserStorageGetSessionItem } from '@/utils/browserStorage';
import { createLazyInstance } from '@/utils/lazy';
import { standardValidate } from '@/utils/standardValidate';

function logout() {
  useTokenStore().clearToken();
  useIdentityStore().clearCurrentBrand();
  useIdentityStore().clearIdentity();
  useImpersonatorStore().clearImpersonatedUser();
  nextTick(() => {
    window.location.assign('/logout');
  });
}

async function attemptTokenRefresh() {
  const success = await useTokenStore().refreshToken();
  if (success) {
    return true;
  }
  logout();
  return false;
}

async function attemptTokenRefreshIfBadToken(error: AxiosError) {
  const { status, data = {} as any } = error.response ?? {};
  if (status === 401) {
    // Checking if status is 401 should be enough to determine if we need to redirect to login,
    // but just in case, we check the error message to ensure we're only redirecting when token
    // is invalid or expired.
    const tokenErrors = [
      'Authentication token missing or invalid.',
      'Authentication token invalid or expired.', // Deprecated
      'Token Expired.', // Deprecated
      'Invalid Token.', // Deprecated
    ];
    if (tokenErrors.indexOf(data.description) > -1) {
      return attemptTokenRefresh();
    }
  }
  return false;
}

function createAxios(
  baseURL: string,
  {
    includeAuth = true,
    camelizeKeys = false,
    decamelizeRequests = true,
    decamelizeIgnoreKeys,
  }: AxiosOptions = {},
) {
  const ax = axios.create({ baseURL });

  ax.interceptors.request.use(
    (axConfig: InternalAxiosRequestConfig & AxiosOptions) => {
      const decamelizeKeys = isBoolean(axConfig?.decamelizeRequests)
        ? axConfig?.decamelizeRequests
        : decamelizeRequests;
      if (decamelizeKeys) {
        const decamelizeConvert: HumpsProcessor = (key, convert, convertOptions) =>
          decamelizeIgnoreKeys?.includes(key) ? key : convert(key, convertOptions);
        axConfig.params = humps.decamelizeKeys(axConfig.params, decamelizeConvert);
        axConfig.data = humps.decamelizeKeys(axConfig.data, decamelizeConvert);
      }
      if (includeAuth) {
        const token = useTokenStore().token;
        if (token && !axConfig.headers.Authorization) {
          axConfig.headers.Authorization = `Bearer ${token}`;
        }
      }
      const impersonatedUserId = browserStorageGetSessionItem(IMPERSONATED_USER_ID_KEY);
      if (impersonatedUserId) {
        axConfig.headers['X-On-Behalf-Of'] = `${impersonatedUserId}`;
      }
      return axConfig;
    },
    (error) => Promise.reject(error),
  );

  ax.interceptors.response.use(
    async (response) => {
      const requestConfig = response?.config as Pick<AxiosOptions, 'camelizeKeys'> & {
        schema?: StandardSchemaV1;
      };
      const shouldCamelizeKeys = isBoolean(requestConfig?.camelizeKeys)
        ? requestConfig?.camelizeKeys
        : camelizeKeys;
      const data = requestConfig?.schema
        ? await standardValidate(requestConfig.schema, response.data)
        : response.data;
      return {
        ...response,
        data: shouldCamelizeKeys ? humps.camelizeKeys(data) : data,
      };
    },
    async (error) => {
      const tokenStore = useTokenStore();
      if (tokenStore.token && !tokenStore.isShareToken && error.response) {
        // we do const { status, data } = error.response.
        // want to make sure error.response exists.
        const success = await attemptTokenRefreshIfBadToken(error);
        if (success) {
          // if token was refreshed successfully, retry the request
          error.response.config.headers.Authorization = `Bearer ${tokenStore.token}`;
          return new Promise((resolve) => {
            resolve(ax(error.response.config));
          });
        }
      }
      return Promise.reject(error);
    },
  );

  return ax;
}

export function abortApiCall(abortControllerRef: MaybeRefOrGetter<AbortController | null> | null) {
  toValue(abortControllerRef)?.abort();
}

export function refreshAbortController(abortControllerRef: unknown) {
  if (!abortControllerRef || !isRef<AbortController | null>(abortControllerRef)) {
    throw new Error('ref required for refreshAbortController');
  }

  // Cancel last request if present
  abortApiCall(abortControllerRef);

  const newController = new AbortController();
  abortControllerRef.value = newController;
  return newController.signal;
}

export function isCancelError(error: unknown): boolean {
  return axios.isCancel(error) || (error instanceof DOMException && error.name === 'AbortError');
}

export function handleCancelError(error: Error, callback?: () => void) {
  if (!isCancelError(error)) {
    throw error;
  }
  callback?.();
}

/**
 * Type guard for HttpError.
 * Matches the HttpError class and duck-typed objects with the same shape.
 */
function isHttpErrorInstance(error: unknown): error is HttpError {
  return (
    error != null &&
    typeof error === 'object' &&
    'status' in error &&
    'name' in error &&
    (error as HttpError).name === 'HttpError'
  );
}

/**
 * Extract HTTP status code from an error.
 * Works with both AxiosError (current) and HttpError (future).
 * Returns undefined for non-HTTP errors (network errors, cancel errors, etc.)
 */
export function getHttpErrorStatus(error: unknown): number | undefined {
  if (isHttpErrorInstance(error)) return error.status;
  if (error != null && typeof error === 'object' && 'response' in error) {
    return (error as { response?: { status?: number } }).response?.status;
  }
  return undefined;
}

/**
 * Extract response data from an HTTP error.
 * Works with both AxiosError (current) and HttpError (future).
 */
export function getHttpErrorData(error: unknown): unknown {
  if (isHttpErrorInstance(error)) return error.data;
  if (error != null && typeof error === 'object' && 'response' in error) {
    return (error as { response?: { data?: unknown } }).response?.data;
  }
  return undefined;
}

/**
 * Check if an error is an HTTP error with a response status.
 * Works with both AxiosError (current) and HttpError (future).
 */
export function isHttpError(error: unknown): boolean {
  return getHttpErrorStatus(error) !== undefined;
}

/**
 * Extract the `description` field from an HTTP error's response data.
 * The backend consistently returns `{ description: string }` in error responses.
 * Works with both AxiosError (current) and HttpError (future).
 */
export function getHttpErrorDescription(error: unknown): string | undefined {
  const data = getHttpErrorData(error);
  if (data != null && typeof data === 'object' && 'description' in data) {
    return typeof data.description === 'string' ? data.description : undefined;
  }
  return undefined;
}

/**
 * This function can be used to create an axios instance for use with internal APIs
 * This adds handling for authentication, impersonation, and camalization
 *
 * Note: If you are making a call to an external API, use the helpers from external-apis.js
 *
 * @param baseURL base API url to use with request made by this instance
 * @param options axios options
 * @returns {import('axios').AxiosInstance} axios instance
 */
export function createAxiosInstance(baseURL: string, options?: AxiosOptions) {
  return createLazyInstance(createAxios, [baseURL, options]);
}
